/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef, Suspense, Fragment } from 'react';
import { Await, redirect, useRouteLoaderData } from 'react-router';
import type { loader as rootLoader } from '@/root';
import type { Route } from './+types/_app.product.$productId.($variantId)';
import { type ShopperProducts } from '@/scapi';
import { fetchProductById } from '@/lib/api/products.server';
import { fetchCategory } from '@/lib/api/categories.server';
import { fetchWishlistInitialState } from '@/lib/wishlist/fetch-initial-state.server';
import type { WishlistInitialState } from '@/lib/wishlist/state';
import { WishlistProvider } from '@/providers/wishlist';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { siteContext } from '@salesforce/storefront-next-runtime/site-context';
import { getTranslation } from '@salesforce/storefront-next-runtime/i18n';
import ProductView from '@/components/product-view';
import ChildProducts from '@/components/product-view/child-products';
import { isProductSet, isProductBundle } from '@/lib/product/product-utils';
import { resolveCanonicalProductSlug, resolveProductIdFromSlug } from '@/lib/product/slug.server';
import { isDesignModeActive, isPreviewModeActive } from '@salesforce/storefront-next-runtime/design/mode';
import ProductRecommendations from '@/components/product-recommendations';
import { EINSTEIN_RECOMMENDERS } from '@/lib/adapters/engagement/einstein-recommenders';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/use-analytics';
import { Region } from '@/components/region';
import { ProductProvider } from '@/providers/product-context';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { resolveLastComponentTheme } from '@/lib/page-designer/last-component-theme';
import { type DefaultSettingsTheme } from '@/lib/page-designer/default-settings';
import { JsonLd } from '@/components/json-ld';
import { SeoMeta } from '@/components/seo-meta';
import { generateProductSchema, getPrimaryImageUrl } from '@/utils/product-schema';
import { buildPageName, DATA_LAYER_SITE, type RouteAnalytics } from '@/analytics/data-layer';
import { buildCategoryCrumbs, generateBreadcrumbSchema, type BreadcrumbListSchema } from '@/utils/breadcrumb-schema';
import { getPublicOrigin } from '@/utils/schema-url';
import { getDamMainImageUrl } from '@/lib/product/product-record-fields';
import { resolvePageMetaDescription } from '@/lib/seo/description';
import { resolveOgImage } from '@/lib/seo/og-image';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { getLogger } from '@/lib/logger.server';
import { UITarget } from '@/targets/ui-target';
// @sfdc-extension-block-start SFDC_EXT_BOPIS
import { selectedStoreContext } from '@/extensions/store-locator/middlewares/selected-store.server';
import PickupProvider from '@/extensions/bopis/context/pickup-context';
// @sfdc-extension-block-end SFDC_EXT_BOPIS

@PageType({
    name: 'Product Detail Page',
    description: 'Product detail page with product information, images, and recommendations',
    supportedAspectTypes: ['pdp'],
})
@RegionDefinition([
    {
        id: 'promoContent',
        name: 'Promo Content Region',
        description: 'Promotional content region above main product content',
        maxComponents: 1,
    },
    {
        id: 'engagementContent',
        name: 'Engagement Content Region',
        description: 'Engagement content region for recommendations and related products below main content',
        // No maxComponents — merchandisers may stack any number of full-width components here.
    },
])
export class ProductPageMetadata {}

export type ProductPageData = {
    product: ShopperProducts.schemas['Product'];
    category: Promise<ShopperProducts.schemas['Category'] | undefined>;
    page: ReturnType<typeof fetchPageWithComponentData>;
    /** Footer background theme = the theme of the last component (engagementContent). Streams. */
    footerTheme?: Promise<DefaultSettingsTheme>;
    pageKey: string;
    pageUrl: string;
    /** Authored meta description: product page description → short description → none. (UCR-3023) */
    metaDescription?: string;
    /** Social share image: the product's Widen DAM main image, else the Black Magic mark. (UCR-3023) */
    ogImageUrl: string;
    productSchema: Promise<ReturnType<typeof generateProductSchema> | null>;
    /** Breadcrumb JSON-LD (Home → category trail → product) for the PDP (UCR-2783). */
    breadcrumbSchema?: Promise<BreadcrumbListSchema | null>;
    wishlistInitialState: Promise<WishlistInitialState>;
    /** Adobe Analytics data-layer payload for the PDP (UCR-2938). */
    analytics?: RouteAnalytics;
};

/**
 * Server-side loader function that fetches product data and category information.
 * This function runs on the server during SSR and can access cookies for store information.
 *
 * The product is awaited as critical data: a 404 from SCAPI is re-thrown as
 * `Response(message, { status: 404 })` so React Router renders the 404 page with
 * the proper HTTP status (essential for SEO).
 *
 * The variant SKU arrives as the optional `:variantId` path segment
 * (`/product/Living-Soil-Kit/3355101`); legacy `?pid=` URLs are 301'd to that form below (UCR-3032).
 *
 * The first segment is a product id for masters and variants, but a NAME SLUG for standard products
 * (`/product/organic-microbes`, not `/product/3353012`) — resolved back to the id via
 * `resolveProductIdFromSlug`, with the id form 301'd onto the slug form below (UCR-3036).
 *
 * @returns Object containing the resolved product, deferred category, page data, and schema promises
 */
export async function loader(args: Route.LoaderArgs): Promise<ProductPageData> {
    const { request, params, context } = args;
    const logger = getLogger(context);
    const { productId, variantId } = params;
    const requestUrl = new URL(request.url);
    const { searchParams } = requestUrl;

    // Legacy variant URLs (`?pid=<sku>`, the pre-UCR-3032 form) redirect permanently to the path form
    // so crawlers, bookmarks, and already-sent emails consolidate on one clean URL. Only `pid` is
    // dropped — attribution params ride along. Skipped when the path already carries a variant segment:
    // the segment is canonical and a stray `pid` must not append a third segment.
    //
    // Built off `requestUrl.pathname`, so whatever site/locale prefix is in play is preserved without
    // this route having to know about it.
    const legacyPid = searchParams.get('pid');
    if (!variantId && legacyPid) {
        const target = new URL(requestUrl);
        target.searchParams.delete('pid');
        target.pathname = `${requestUrl.pathname.replace(/\/+$/, '')}/${encodeURIComponent(legacyPid)}`;
        logger.debug('Product: redirecting legacy ?pid= URL', { productId, legacyPid });
        throw redirect(`${target.pathname}${target.search}`, 301);
    }

    logger.debug('Product: loader starting', { productId, variantId: variantId || undefined });

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    const selectedStoreInfo = context.get(selectedStoreContext);
    // @sfdc-extension-block-end SFDC_EXT_BOPIS

    // Get currency from context for product pricing
    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        logger.error('Product: site context is not available');
        throw new Response('Site context is not available', { status: 500 });
    }
    const { currency } = siteCtx;

    // A standard product's URL names it (`/product/organic-microbes`) rather than exposing its SKU
    // (UCR-3036), so the first segment may be a slug that SCAPI cannot address. Resolve it to a
    // product id first; a segment that is no known slug — every master id, every legacy SKU URL —
    // resolves to `undefined` and travels on unchanged. Skipped when the path already carries a
    // variant segment: only masters have variants, and masters keep their id in the URL.
    const slugProductId = variantId ? undefined : await resolveProductIdFromSlug(context, productId);

    // Resolve the product critically. A 404 here must propagate as Response(404)
    // so the route error boundary renders the 404 page with the correct HTTP status.
    // A bogus variant segment therefore 404s rather than silently falling back to the master.
    const productLookupId = variantId || slugProductId || productId;

    let product: ShopperProducts.schemas['Product'] | null;
    try {
        product = await fetchProductById(context, productLookupId, {
            expand: [
                'availability', // <-- TTL = 60s (!)
                'bundled_products',
                'images',
                'options',
                'page_meta_tags',
                'prices', // <-- TTL = 900s
                'promotions', // <-- TTL = 900s
                'set_products',
                'variations',
            ],
            allImages: true,
            perPricebook: true,
            ...(currency ? { currency } : {}),
            // @sfdc-extension-block-start SFDC_EXT_BOPIS
            // Include inventoryIds parameter when store is selected
            ...(selectedStoreInfo?.inventoryId ? { inventoryIds: [selectedStoreInfo.inventoryId] } : {}),
            // @sfdc-extension-block-end SFDC_EXT_BOPIS
        });
    } catch (e) {
        if (e instanceof NormalizedApiError && e.status) {
            throw new Response(e.message, { status: e.status });
        }
        throw new Response('Internal Server Error', { status: 500 });
    }

    if (!product) {
        throw new Response('Product not found', { status: 404 });
    }

    // One canonical URL per standard product (UCR-3036). The `<link rel="canonical">` tag is
    // generated centrally in `root.tsx` from the REQUEST url, so — exactly as the Learn articles do —
    // 301'ing the non-canonical form is what makes the canonical correct, and it consolidates every
    // already-sent SKU link (emails, crawled pages, the PD link picker, cart and order history) onto
    // the named URL. `resolveCanonicalProductSlug` returns a slug only when it provably maps back to
    // this product, so this can never redirect onto a different product or into a 404.
    //
    // Built off `requestUrl.pathname` so any site/locale prefix survives untouched, and skipped in
    // Page Designer edit/preview where the merchandiser's `mode`/`pdToken` URL must be preserved.
    const canonicalSlug = variantId ? undefined : await resolveCanonicalProductSlug(context, product);
    const previewing = isDesignModeActive(request) || isPreviewModeActive(request);
    if (canonicalSlug && canonicalSlug !== productId && !previewing) {
        const marker = '/product/';
        const markerIndex = requestUrl.pathname.lastIndexOf(marker);
        if (markerIndex >= 0) {
            const pathname = `${requestUrl.pathname.slice(0, markerIndex + marker.length)}${canonicalSlug}`;
            logger.debug('Product: redirecting to the canonical name URL', { productId, canonicalSlug });
            throw redirect(`${pathname}${requestUrl.search}`, 301);
        }
    }

    // Build the deferred category promise. Category is optional context for the
    // breadcrumbs — failures degrade silently via the route-level <Await errorElement={null}>.
    const categoryPromise: Promise<ShopperProducts.schemas['Category'] | undefined> = (async () => {
        if (product.primaryCategoryId) {
            return fetchCategory(context, product.primaryCategoryId, 1);
        }
        // For variant products, try to get the master product's category.
        if (product.master?.masterId) {
            const masterProduct = await fetchProductById(context, product.master.masterId, {
                ...(currency ? { currency } : {}),
            });
            if (masterProduct?.primaryCategoryId) {
                return fetchCategory(context, masterProduct.primaryCategoryId, 1);
            }
        }
        return undefined;
    })();

    // Resolved public origin, not the lambda-internal request origin, so og:url matches the canonical
    // link and the JSON-LD `url` (UCR-3023).
    const pageUrl = buildCanonicalUrl(getPublicOrigin(request), requestUrl.pathname, requestUrl.search);

    // Generate product schema in loader (server-side) for SEO.
    // Wrapped in a Promise so it can be rendered through Suspense without blocking
    // the loader response. The inner try/catch logs synchronous schema-generation failures
    // (this is local computation, not a SCAPI call — `fetchProductById` already logs SCAPI
    // errors at the API layer) so we keep visibility on rare malformed-input bugs.
    // Render-time failures degrade silently via the route-level <Await errorElement={null}>.
    const productSchemaPromise: Promise<ReturnType<typeof generateProductSchema> | null> = Promise.resolve().then(
        () => {
            try {
                // Use public origin from request headers instead of request.url
                // to avoid exposing internal AWS Lambda URLs in schema
                const publicOrigin = getPublicOrigin(request);
                const url = new URL(request.url);
                const productUrl = `${publicOrigin}${url.pathname}${url.search}`;
                return generateProductSchema(product, productUrl);
            } catch (error) {
                logger.error('Error generating product schema in loader', { error });
                return null;
            }
        }
    );

    // Breadcrumb JSON-LD (UCR-2783): the trail isn't shown on the PDP (breadcrumbs are hidden per
    // UCR-2697), but the schema still reflects the site hierarchy — home → category ancestors →
    // primary category → product — for Breadcrumb rich results. Derived off the (deferred) category
    // promise; fail-soft to null so a category-fetch failure just omits the breadcrumb. Shares
    // `buildCategoryCrumbs` with the PLP so both pages describe the same chain (UCR-3005).
    const homeName = getTranslation(context).t('category:breadcrumbs.home');
    const breadcrumbSchemaPromise: Promise<BreadcrumbListSchema | null> = categoryPromise
        .then((category) => {
            const publicOrigin = getPublicOrigin(request);
            const url = new URL(request.url);
            const productUrl = `${publicOrigin}${url.pathname}${url.search}`;
            const crumbs = buildCategoryCrumbs({
                category,
                origin: publicOrigin,
                currentPageUrl: productUrl,
                homeName,
            });
            crumbs.push({ name: product.name ?? product.id, url: productUrl });
            return generateBreadcrumbSchema(crumbs);
        })
        .catch((error) => {
            logger.error('Error generating breadcrumb schema in loader', { error });
            return null;
        });

    // Fetch page data from Page Designer API with nested componentData promises. Streamed (not awaited)
    // so the product content renders without waiting on the PD fetch; errors resolve to empty data.
    const page = fetchPageWithComponentData(args, {
        aspectType: 'pdp',
        productId: productLookupId,
        // Lets the manifest resolver fall back to a category-level PDP
        // assignment when no page is assigned to this product directly.
        ...(product.primaryCategoryId ? { categoryId: product.primaryCategoryId } : {}),
    });

    // Analytics data layer (UCR-2938): PDP page + catalog context. `product` is already resolved
    // (awaited above as critical data). `rating` stays empty until a reviews integration lands.
    const analytics: RouteAnalytics = {
        page: { pageName: buildPageName('product', product.id), pageType: 'product' },
        product: {
            sku: product.id,
            name: product.name ?? '',
            // The SDR `brand` (v10/v9) is the site brand id "blackmagic" (single-brand site), not the
            // product's brand attribute ("Black Magic") — matches Tony's spec example.
            brand: DATA_LAYER_SITE,
            price: typeof product.price === 'number' ? product.price.toFixed(2) : '',
            rating: '',
        },
        pageEvent: 'productView',
    };

    return {
        product,
        category: categoryPromise,
        page,
        analytics,
        // Footer continues the theme of the last component in the bottom-most region
        // (engagementContent). Derived off the streamed page; fail-soft to light on error.
        footerTheme: page
            .then((p) => resolveLastComponentTheme(p, ['promoContent', 'engagementContent']))
            .catch(() => 'light' as const),
        pageKey: productId,
        pageUrl,
        // Product record only — hence the explicit `null` page. The PDP's Page Designer page is a
        // shared `pdp` aspect template covering every product, and it is deliberately left as an
        // unresolved promise below so product content isn't blocked behind it; awaiting it purely to
        // read a description would reintroduce the first-byte problem the homepage guards against.
        // `shortDescription` is rich text in Business Manager, so the resolver strips markup and caps
        // the length before it reaches a `content=` attribute. (UCR-3023)
        metaDescription: resolvePageMetaDescription(null, product.pageDescription || product.shortDescription),
        // The Widen DAM main image (`pdpMainImage`) is the source of truth for product imagery
        // (UCR-3003) — the same image the product JSON-LD uses — so the social card and the rich
        // result agree. `imageGroups` (internal SFCC/DIS) is the fallback. (UCR-3023)
        ogImageUrl: resolveOgImage({
            origin: getPublicOrigin(request),
            contentImageUrl: getDamMainImageUrl(product) ?? getPrimaryImageUrl(product),
        }),
        productSchema: productSchemaPromise,
        breadcrumbSchema: breadcrumbSchemaPromise,
        wishlistInitialState: fetchWishlistInitialState(context),
    };
}

/**
 * Control when the loader re-runs.
 * https://reactrouter.com/start/data/route-object#shouldrevalidate
 *
 * Since UCR-3032 the variant SKU is a path segment, so a variant switch IS a pathname change and is
 * covered by the pathname check below — the loader must re-run to pick up that variant's price and
 * inventory. It does not cause a skeleton: the page's remount key is the MASTER segment
 * (`pageKey`), which is unchanged by a variant switch, so React keeps the mounted tree and React
 * Router holds the previous UI while the loader is in flight.
 *
 * Partial variation selections (a colour chosen but not yet a size) still live in search params and
 * deliberately do NOT revalidate — no variant is resolved yet, so there is nothing new to fetch.
 */
export function shouldRevalidate({
    currentUrl,
    nextUrl,
    defaultShouldRevalidate,
}: {
    currentUrl: string;
    nextUrl: string;
    defaultShouldRevalidate: boolean;
}) {
    const currentUrlObj = new URL(currentUrl);
    const nextUrlObj = new URL(nextUrl);

    // Revalidate if the pathname changes — a different product OR a different variant segment.
    if (currentUrlObj.pathname !== nextUrlObj.pathname) {
        return true;
    }

    // If defaultShouldRevalidate is true (e.g., from explicit revalidator.revalidate() call),
    // allow it to proceed even if URL hasn't changed
    // This allows store changes to trigger revalidation
    if (defaultShouldRevalidate) {
        return true;
    }

    // Don't revalidate for other search parameter changes (color, size, etc.)
    return false;
}

function ProductContent({
    product,
    url,
    description,
    ogImageUrl,
}: {
    product: ShopperProducts.schemas['Product'];
    url: string;
    description?: string;
    ogImageUrl: string;
}) {
    const analytics = useAnalytics();
    const lastTrackedProductIdRef = useRef<string | null>(null);

    // Track product view on mount and whenever productData changes
    useEffect(() => {
        // Only track if we haven't already tracked this product
        if (product.id !== lastTrackedProductIdRef.current) {
            void analytics.trackViewProduct({
                product,
            });
            lastTrackedProductIdRef.current = product.id;
        }
    }, [analytics, product]);

    const isProductASet = isProductSet(product);
    const isProductABundle = isProductBundle(product);

    return (
        <ProductProvider product={product}>
            <SeoMeta
                title={product.name}
                description={description}
                openGraph={{
                    type: 'product',
                    url,
                    image: ogImageUrl,
                }}
            />
            <div className="space-y-8">
                {isProductASet || isProductABundle ? (
                    <>
                        <ProductView product={product} />
                        <ChildProducts parentProduct={product} />
                    </>
                ) : (
                    <ProductView product={product} />
                )}

                <UITarget targetId="sfcc.pdp.reviews.qna" />
            </div>
        </ProductProvider>
    );
}

/**
 * Product detail shell that composes the page layout with granular Suspense boundaries.
 * Regions render independently (they manage their own async via Suspense/Await),
 * while the core product content renders synchronously from the resolved loader data.
 */
function ProductDetailView({ loaderData }: { loaderData: ProductPageData }) {
    const { t } = useTranslation('product');
    const content = (
        <div className="pdp-dark -mt-8 min-h-screen bg-background text-foreground">
            <div className="section-container pt-0 pb-4 lg:pt-12 lg:pb-8">
                {/* Promo Content Region - Promotional content above main product */}
                <Region className="mb-8" page={loaderData.page} regionId="promoContent" />

                {/* Breadcrumbs intentionally hidden on the PDP per design (UCR-2697). */}

                {/* Main Product Content — product is resolved synchronously by the loader */}
                <ProductContent
                    product={loaderData.product}
                    url={loaderData.pageUrl}
                    description={loaderData.metaDescription}
                    ogImageUrl={loaderData.ogImageUrl}
                />
            </div>

            {/* Engagement Content Region — rendered OUTSIDE the .section-container so authored Page
                Designer components can span the full window width (their own w-full sections bleed
                edge-to-edge, with interior content constrained by their own inner .section-container). */}
            <Region
                className="mt-16"
                page={loaderData.page}
                regionId="engagementContent"
                errorElement={
                    // Client-fetched recommendation fallback (shown when the engagement region is
                    // empty). These carousels are not full-bleed, so they keep their own constrained
                    // .section-container. Each owns its top spacing (mt-16) so empty recommenders —
                    // which render null — contribute zero height instead of leaving a gap, and the
                    // loading skeleton is suppressed so they don't flash a "ghost" before resolving.
                    <div className="section-container">
                        <ProductRecommendations
                            recommenderName={EINSTEIN_RECOMMENDERS.PDP_COMPLETE_SET}
                            recommenderTitle={t('recommendations.completeTheLook')}
                            className="mt-16 max-w-none px-0"
                            hideLoadingSkeleton
                        />
                        <ProductRecommendations
                            recommenderName={EINSTEIN_RECOMMENDERS.PDP_MIGHT_ALSO_LIKE}
                            recommenderTitle={t('recommendations.youMightAlsoLike')}
                            className="mt-16 max-w-none px-0"
                            hideLoadingSkeleton
                        />
                        <ProductRecommendations
                            recommenderName={EINSTEIN_RECOMMENDERS.PDP_RECENTLY_VIEWED}
                            recommenderTitle={t('recommendations.recentlyViewed')}
                            className="mt-16 max-w-none px-0"
                            hideLoadingSkeleton
                        />
                    </div>
                }
            />
        </div>
    );

    let finalContent = content;
    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    finalContent = <PickupProvider>{finalContent}</PickupProvider>;
    // @sfdc-extension-block-end SFDC_EXT_BOPIS

    return finalContent;
}

/**
 * Product page component that displays a product with its details and category breadcrumbs.
 * The page key ensures React only remounts when navigating to a different product, not variants.
 * @returns JSX element representing the product page with Suspense boundary
 */
export default function ProductPage({ loaderData }: { loaderData: ProductPageData }) {
    // Use pageKey from loaderData to force remount only when the MASTER productId changes.
    // This prevents showing skeleton when switching variants (the `:variantId` path segment).
    const pageKey = loaderData.pageKey;

    const rootData = useRouteLoaderData<typeof rootLoader>('root');
    const nonce = rootData?.nonce ?? undefined;

    return (
        <WishlistProvider initialState={loaderData.wishlistInitialState}>
            <Fragment key={pageKey}>
                <ProductDetailView loaderData={loaderData} />

                {/* Product JSON-LD Schema for SEO - render after page content so it appears at end of body flow.
                    JSON-LD is non-critical: errorElement renders nothing so a schema-generation failure
                    silently degrades to no <script> tag. */}
                <Suspense fallback={null}>
                    <Await resolve={loaderData.productSchema} errorElement={null}>
                        {(productSchema) =>
                            productSchema ? <JsonLd data={productSchema} id="product-schema" nonce={nonce} /> : null
                        }
                    </Await>
                </Suspense>

                {/* Breadcrumb JSON-LD (UCR-2783) — same non-critical, silently-degrading pattern. */}
                {loaderData.breadcrumbSchema && (
                    <Suspense fallback={null}>
                        <Await resolve={loaderData.breadcrumbSchema} errorElement={null}>
                            {(breadcrumbSchema) =>
                                breadcrumbSchema ? (
                                    <JsonLd data={breadcrumbSchema} id="breadcrumb-schema" nonce={nonce} />
                                ) : null
                            }
                        </Await>
                    </Suspense>
                )}
            </Fragment>
        </WishlistProvider>
    );
}
